import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewContainerRef,
  inject,
  ComponentRef,
} from '@angular/core';
import { TooltipChartsComponent, TTooltipChartsItem } from '@shared/components/ui/tooltip-charts';

/**
 * Directive to display TooltipChartsComponent on hover and handle automatic positioning
 */
@Directive({
  selector: '[libTooltipChartsHover]',
  standalone: true,
  providers: [],
})
export class TooltipChartsHoverDirective implements OnDestroy {
  /** Tooltip title (i18n key or plain string) */
  @Input('tooltipChartTitle') title?: string;
  /** Tooltip sidebar color */
  @Input('tooltipChartColor') color?: string;
  /** Tooltip content (array of label/value rows or a simple string) */
  @Input('tooltipChartContent') content: TTooltipChartsItem[] | string = [];
  /** Whether the tooltip should hide when hovering over a nested child that also has tooltip */
  @Input('tooltipChartIgnoreChildren') ignoreChildren: boolean = true;

  public readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly parentDirective = inject(TooltipChartsHoverDirective, { skipSelf: true, optional: true });
  private readonly vcr = inject(ViewContainerRef);
  private compRef: ComponentRef<TooltipChartsComponent> | null = null;

  private hiddenByChild = false;

  /**
   * Show tooltip and start tracking mouse movement.
   * @param event Mouse event triggered on hover.
   */
  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent): void {

    if (this.ignoreChildren && this.parentDirective) {
      this.parentDirective.hideTooltip();
    }
    if (this.compRef) return;
    this.compRef = this.vcr.createComponent(TooltipChartsComponent, { index: 0 });


    this.compRef.setInput('title', this.title);
    const contentArr: TTooltipChartsItem[] = Array.isArray(this.content) ? this.content as TTooltipChartsItem[] : [{ value: this.content as string }];
    this.compRef.setInput('content', contentArr);
    if (this.color) this.compRef.setInput('color', this.color);
    const initialPos = { top: event.clientY + 12, left: event.clientX + 12 };
    this.compRef.setInput('position', initialPos);
    this.compRef.setInput('show', true);

    const el = this.compRef.location.nativeElement as HTMLElement;
    el.style.position = 'fixed';
    el.style.top = '0';
    el.style.left = '0';

    window.addEventListener('mousemove', this.mouseMoveHandler, { passive: true });
  }

  /**
   * Destroy tooltip and restore parent tooltip if applicable.
   * @param event Mouse event triggered when leaving the host element.
   */
  @HostListener('mouseleave', ['$event'])
  onMouseLeave(event: MouseEvent): void {
    this.destroyTooltip();

    if (this.ignoreChildren && this.parentDirective) {
      const related = event.relatedTarget as Node;
      if (related && this.hostEl.nativeElement.contains(related)) {
        this.parentDirective.showTooltip();
      }
    }
  }

  /**
   * Update tooltip position to follow the cursor.
   * @param event Mouse event used for updating tooltip position.
   */
  private readonly mouseMoveHandler = (event: MouseEvent): void => {
    if (this.ignoreChildren && this.hostEl.nativeElement.contains(event.target as Node)) {
      const el = (event.target as HTMLElement).closest('[libTooltipChartsHover]');
      if (el && el !== this.hostEl.nativeElement) {

        if (!this.hiddenByChild && this.compRef) {
          this.compRef.setInput('show', false);
          this.hiddenByChild = true;
        }
        return;
      } else {

        if (this.hiddenByChild && this.compRef) {
          this.compRef.setInput('show', true);
          this.hiddenByChild = false;
        }
      }
    }
    if (!this.compRef) return;
    const position = { top: event.clientY + 12, left: event.clientX + 12 };
    this.compRef.setInput('position', position);
  };

  /**
   * Destroy the tooltip component and remove event listeners.
   */
  private destroyTooltip(): void {
    window.removeEventListener('mousemove', this.mouseMoveHandler);
    if (this.compRef) {
      this.compRef.destroy();
      this.compRef = null;
    }
  }

    /** Hide tooltip without destroying (for nested priority) */
  public hideTooltip(): void {
    if (this.compRef) {
      this.compRef.setInput('show', false);
      this.hiddenByChild = true;
    }
  }

  /** Show tooltip hidden by nested child */
  public showTooltip(): void {
    if (this.compRef && this.hiddenByChild) {
      this.compRef.setInput('show', true);
      this.hiddenByChild = false;
    }
  }

  /**
   * Cleanup when directive is destroyed.
   */
  ngOnDestroy(): void {
    this.destroyTooltip();
  }
}
